import TableOnly from "components/DataTable/TableOnly";
import { useWoStatuses, woStatusLabel, woStatusStyle } from "utils/woStatus";
import {
  APPROVAL_TABS,
  APPROVAL_TAB_LABELS,
  approvalBadgeTone,
  ADMIN_REVIEW_ALL,
} from "utils/approvalStage";
import RefreshButton from "components/RefreshButton/RefreshButton";
import PaginationWithState from "components/Pagination/PaginationWithState";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useState } from "react";
import { Button, Dropdown, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import {
  convertAttendanceRecordToTime,
  convertSqlTimestampToDate,
  truncateString,
} from "utils/UtilityFuncs";
import SendWoApproval from "../WorkOrders/WorkOrderDetails/LotApproval/SendWoApproval";

export default function Approvals() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state?.user?.userInfo);
  // Every status from Settings -> WO Statuses, Approvals included this time
  // (user, 2026-10-29); it used to offer pending / processing / completed.
  const woStatuses = useWoStatuses();
  /*   ALL STATES
   ********************************************* */
  const [filterBy, setFilterBy] = useState({
    name: "Filter by All",
    value: null,
  });
  const [approvalData, setApprovalData] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});

  // TableOnly writes the clicked column into the URL (?sort=&order=). We read
  // it back and hand it to the API -- sorting has to run in SQL, otherwise
  // we'd only be reordering the ten rows already on screen.
  const [searchParams] = useSearchParams();
  const sortBy = searchParams.get("sort") || undefined;
  const sortDir = searchParams.get("order") || undefined;
  const [showDetailsAddModal, setShowDetailsAddModal] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);

  const [contactData, setContactData] = useState(null);

  const [showJobNameModal, setShowJobNameModal] = useState(false);
  const [jobNamesFormData, setJobNamesFormData] = useState(null);
  const [selectedWOItem, setSelectedWOItem] = useState(null);

  // "approved" is deliberately absent: it and "approved_by_time" select the
  // same set, so only the age-ordered one is shown and it carries the plain
  // "Approved" label. The backend still counts both.
  // Tabs, their order and their labels all come from the shared stage
  // constants now. Each one is a single approvalStage value on the server
  // rather than its own hand-written SQL predicate.
  const waStatusOptions = APPROVAL_TABS;
  const [waStatus, setWaStatus] = useState("pending");

  // All's badge: the sum of the other five, which is exactly what it lists.
  const countFor = (status) =>
    status === "all"
      ? APPROVAL_TABS.filter((t) => t !== "all").reduce(
          (sum, t) => sum + (statusCounts[t] || 0),
          0,
        )
      : statusCounts[status] || 0;

  const formatWaStatusText = (status) =>
    APPROVAL_TAB_LABELS[status] ||
    status.charAt(0).toUpperCase() + status.slice(1);

  const handleWaStatusChange = (status) => {
    setWaStatus(status);
    setPageNumber(1);
  };

  const [loading, setLoading] = useState(false);

  const [locationsList, setLocationsList] = useState([]);
  const [location, setLocation] = useState("");

  // Pagination/Search states
  const [pageLimit, setPageLimit] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  /*   All Functions
   ********************************************* */
  const getApprovalData = useCallback(
    async ({ clearSearch = false, query, page } = {}) => {
      try {
        setLoading(true);
        const usedQuery = clearSearch
          ? ""
          : typeof query !== "undefined"
            ? query
            : "";
        const usedPage = page ?? pageNumber;
        const { data } = await API.post(
          "/wo-lot-approvals?page=" + usedPage + "&limit=" + pageLimit,
          {
            query: usedQuery,
            // Every tab, All included, lists pending work orders only: All is
            // the other five tabs put together (user, 2026-10-21).
            woStatus: "pending",
            waStatus: waStatus || "all",
            approvalStatus: waStatus === "responded" ? "responded" : undefined,
            filterBy:
              usedQuery && usedQuery !== "" ? filterBy.value : undefined,
            locationId: location || undefined,
            sortBy,
            sortDir,
          },
          { headers: authHeader() },
        );

        if (data?.success) {
          // API returns { success, data, total, page, limit, totalPages, statusCounts }

          // Changing a filter can shrink the result set below the page we're
          // sitting on -- the server correctly returns nothing for page 3 of
          // a 1-page result, which rendered as a blank table. Drop back to
          // page 1; the effect below refetches.
          const pages = data?.totalPages || 0;
          if (pages > 0 && usedPage > pages) {
            setPageNumber(1);
            return;
          }

          setApprovalData(data?.data || []);
          setTotalPages(pages);
          if (data?.statusCounts) {
            setStatusCounts(data.statusCounts);
          }
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to fetch",
              type: "error",
            }),
          );
        }
      } catch (err) {
        dispatch(setNotification({ message: err.message, type: "error" }));
      } finally {
        setLoading(false);
      }
    },
    [
      pageNumber,
      pageLimit,
      waStatus,
      location,
      filterBy.value,
      sortBy,
      sortDir,
      dispatch,
    ],
  );

  const getAllLocations = async () => {
    try {
      const { data } = await API.get("/locations", { headers: authHeader() });
      setLocationsList(data);
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  // Delete Approval
  const deleteApprovalData = async (id) => {
    try {
      setLoading(true);
      const { data } = await API.delete("/wo-lot-approval/" + id, {
        headers: authHeader(),
      });
      if (data?.success) {
        // refresh current page
        getApprovalData();
        dispatch(
          setNotification({
            message: data?.message || "Deleted",
            type: "success",
          }),
        );
        return;
      }
      dispatch(
        setNotification({
          message: data?.message || "Failed to delete",
          type: "error",
        }),
      );
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  const hideDetailsModal = () => {
    setShowDetailsAddModal(false);
    setSelectedApproval(null);
  };

  const updateWOStatus = async (id, status) => {
    try {
      setLoading(true);
      const { data } = await API.patch(
        "/work-order-status",
        {
          id,
          WOStatus: status,
        },
        {
          headers: authHeader(),
        },
      );
      if (data?.success) {
        // refresh data
        getApprovalData();
        dispatch(
          setNotification({
            message: data?.message || "Work order status updated",
            type: "success",
          }),
        );
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  const updateAdminReviewed = async (id, adminReviewed) => {
    try {
      setLoading(true);
      const { data } = await API.patch(
        "/work-order-admin-reviewed",
        {
          id,
          adminReviewed,
        },
        {
          headers: authHeader(),
        },
      );
      if (data?.success) {
        // refresh data
        getApprovalData();
        dispatch(
          setNotification({
            message: data?.message || "Admin status updated",
            type: "success",
          }),
        );
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Failed to update Admin status",
            type: "error",
          }),
        );
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  const handleJobNameDoubleClick = (item) => {
    setSelectedWOItem(item);
    setJobNamesFormData({
      jobName: item?.WorkOrder?.jobName,
      subJobName: item?.WorkOrder?.subJobName,
    });
    setShowJobNameModal(true);
  };

  const UpdateJobNames = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { data } = await API.patch(
        "/work-orders",
        {
          data: {
            jobName: jobNamesFormData.jobName,
            subJobName: jobNamesFormData.subJobName,
          },
          id: selectedWOItem?.WorkOrder?.id,
        },
        { headers: authHeader() },
      );

      if (data?.message?.type === "success") {
        await getApprovalData();
        setShowJobNameModal(false);
        setLoading(false);
        return;
      }
      setShowJobNameModal(false);
      setLoading(false);
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
      setLoading(false);
    }
  };

  // checkForPartialApproved and checkForAllApproved used to live here: ~230
  // lines re-deriving, in the browser, the same thing the tab query derived
  // in SQL. They are gone -- the Customer badge reads WorkOrders.approvalStage
  // now. That second derivation is why a row could read Approved here while
  // the tab put it elsewhere, and why every never-sent order showed
  // "Partially" until 2026-09-04: they tested
  //   approval.shippingAddressStatus !== "pending_approval"
  // against a work-order row that carries no approval fields at all, and
  // undefined satisfies it.

  // hasAnyRejectedStatus lived here. It is now hasRejectedOn in
  // utils/approvalStage.js, shared with the work orders list and the quick
  // view -- all three had their own copy, and the other two did not check
  // product statuses, so the same order could read Rejected here and
  // Pending there.

  // get contactData for selected approval
  useEffect(() => {
    if (selectedApproval && showDetailsAddModal) {
      let _contactsData =
        selectedApproval?.WorkOrder?.contactData &&
        typeof selectedApproval?.WorkOrder?.contactData === "string"
          ? JSON.parse(selectedApproval?.WorkOrder?.contactData)
          : selectedApproval?.WorkOrder?.contactData;
      setContactData(_contactsData);
    } else {
      setContactData(null);
    }
  }, [selectedApproval, showDetailsAddModal]);

  // A new sort applies to the whole result set, so page 3 of the old order is
  // meaningless -- go back to the first page.
  useEffect(() => {
    setPageNumber(1);
  }, [sortBy, sortDir]);

  useEffect(() => {
    let timeout = setTimeout(() => {
      getApprovalData();
    }, 50);
    return () => clearTimeout(timeout);
  }, [getApprovalData, location]);

  useEffect(() => {
    let timeout = setTimeout(() => {
      getAllLocations();
    }, 50);
    return () => clearTimeout(timeout);
  }, []);

  /*   Table functions
   ********************************************* */
  let filters = [
    { name: "WA-ID", value: "approvalId" },
    { name: "W-ID", value: "woId" },
    { name: "Customer Name", value: "customerName" },
    { name: "Job Name", value: "jobName" },
  ];

  const toSentenceCase = (text) =>
    text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

  const getLocationInitials = (id, name) => {
    if (!name) return "";
    const words = name.split(" ");
    let initials = words.map((word) => word.charAt(0).toUpperCase());
    return `${id}-${initials.join("")}`;
  };

  const buttons = () => (
    <div className="d-flex flex-grow-1 justify-content-end ">
      <div className="d-flex align-items-center me-2">
        <label className="form-label mb-0 fw-bold me-2">Location:</label>
        <select
          className="form-control"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          style={{ width: 200 }}
        >
          <option value="">All Locations</option>
          <option value="no-location">No Location</option>
          {locationsList?.map((item) => (
            <option key={item.id} value={item.id}>
              {item?.locationName}
            </option>
          ))}
        </select>
        {location !== "" && (
          <button
            type="button"
            className="btn btn-outline-danger ms-1"
            onClick={() => setLocation("")}
          >
            Clear
          </button>
        )}
      </div>
      <button
        onClick={(e) => {
          e.preventDefault();
          getApprovalData({ clearSearch: true });
        }}
        className="btn btn-success"
      >
        Refresh
      </button>
      {/* FilterBy */}
      <button
        type="button"
        className="px-1 bg-primary btn text-dark bg-opacity-10 border border-primary rounded-end mx-2 rounded-start"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        {filterBy ? filterBy.name : "Filter by All"}
      </button>
      <div className="dropdown-menu">
        <button
          onClick={() => setFilterBy({ name: "Filter by All", value: null })}
          className="dropdown-item"
        >
          Filter by All
        </button>
        {filters.map((item, index) => (
          <button
            key={index}
            onClick={() => setFilterBy(item)}
            className="dropdown-item"
          >
            {item.name}
          </button>
        ))}
      </div>
    </div>
  );

  const table_head = [
    { name: "W-ID", value: "woId" },
    { name: "Customer Name", value: "customerName" },
    { name: "Job Name", value: "jobName" },
    // { name: "Sub Job", value: "subJob" },
    { name: "Wo Status", value: "woStatus", tight: true },
    { name: "Customer", value: "allApproved", tight: true },
    { name: "Admin", value: "adminReviewed", tight: true },
    { name: "Location", value: "locationId" },
    {
      name: "Created At",
      value: "createdAt",
    },
    {
      name: "Last Opened",
      value: "lastOpened",
    },
    {
      name: "Last Sent",
      value: "approvalSentTime",
    },
    // Initials only -- the column sits between two wide ones and a full name
    // would push Actions off the edge on a laptop. The full name is on the
    // cell's title, which is where you look when the initials are ambiguous.
    { name: "Sent By", value: "approvalSentBy", tight: true },
    { name: "Actions", value: "Actions" },
  ];

  /**
   * "Ahtisham Akram" -> "AA". First letter of the first name and of the last.
   *
   * Falls back rather than showing nothing useful: one name gives one letter,
   * and a user with neither (or an approval never sent) gives a dash. The
   * server already joins the sender -- see the `user` include in getApprovals
   * -- so this is only ever formatting.
   */
  const senderInitials = (u) => {
    if (!u) return null;
    const first = String(u.firstName || "").trim();
    const last = String(u.lastName || "").trim();
    const letters = [first.charAt(0), last.charAt(0)].filter(Boolean).join("");
    return letters ? letters.toUpperCase() : null;
  };

  const senderFullName = (u) =>
    [u?.firstName, u?.lastName].filter(Boolean).join(" ").trim();

  // adminReviewed is a free-text column, so a value we don't know about
  // falls back to the neutral badge rather than being mislabelled.
  const adminReviewedVariant = (status) => {
    if (status === "approved") return "success";
    if (status === "rejected") return "danger";
    if (status === "hold") return "warning";
    return "info";
  };

  const table_body = (item, index) => {
    // The Customer badge reads the stored STAGE, via the same helper the work
    // orders list and the quick view use. Before the stage column this
    // decision existed in FIVE places, each re-deriving it from the approval
    // cards and each free to disagree with the others and with the tab query.
    //
    // This screen shows a never-sent order as Pending rather than the grey
    // "Not sent" the other two use -- deliberate, and the reason the helper
    // hands back a tone instead of finished text.
    const tone = approvalBadgeTone(item);

    let approvalBadgeClass = "bg-info";
    let approvalBadgeText = "Pending";
    let approvalBadgeStyle;

    if (tone === "rejected") {
      approvalBadgeClass = "bg-danger";
      approvalBadgeText = "Rejected";
    } else if (tone === "approved") {
      approvalBadgeClass = "bg-success";
      approvalBadgeText = "Approved";
    } else if (tone === "partial") {
      approvalBadgeClass = "border border-success border-3 text-dark";
      approvalBadgeText = "Partially";
      approvalBadgeStyle = { backgroundColor: "#ffff00" };
    }

    return (
      <tr className="" key={item.id}>
        <td className="align-middle py-1 text-nowrap">W-{item.woId}</td>
        <td className="align-middle py-1 text-truncate">
          <Link
            to={`/customer/details/${item?.WorkOrder?.customer?.customerId}`}
            className="btn px-1 btn-sm btn-info me-1 py-0"
          >
            {/* <div className="text-nowrap"> W-{item.woId}</div> */}
            View
          </Link>
          {truncateString(item?.WorkOrder?.customer?.customerName, 45) || "-"}
        </td>
        <td
          onDoubleClick={() => handleJobNameDoubleClick(item)}
          className="align-middle py-1 text-truncate text-nowrap"
        >
          {truncateString(item?.WorkOrder?.jobName, 30) || "-"}
        </td>

        {/* <td className="align-middle py-1">
        {truncateString(item?.WorkOrder?.subJobName, 50) || "-"}
      </td> */}
        <td className="align-middle py-1 px-0 pe-1">
          {/* <span
          className={`badge px-2 py-1 ${
            item?.WorkOrder?.WOStatus === "pending"
              ? "bg-info"
              : item?.WorkOrder?.WOStatus === "processing"
              ? "bg-warning"
              : item?.WorkOrder?.WOStatus === "completed"
              ? "bg-success"
              : "bg-secondary"
          }`}
        >
          {item?.WorkOrder?.WOStatus}
        </span> */}
          <Dropdown>
            <Dropdown.Toggle
              className="px-2 py-0 rounded-3"
              style={woStatusStyle(woStatuses, item?.WorkOrder?.WOStatus)}
            >
              {woStatusLabel(woStatuses, item?.WorkOrder?.WOStatus) ||
                toSentenceCase(item?.WorkOrder?.WOStatus)}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {woStatuses.map((status) => (
                <Dropdown.Item
                  key={status.value}
                  onClick={() => updateWOStatus(item?.woId, status.value)}
                >
                  {status.name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </td>
        <td className="align-middle py-1 px-0">
          <span
            className={`badge px-2 py-1 ${approvalBadgeClass}`}
            style={approvalBadgeStyle}
          >
            {approvalBadgeText}
          </span>
        </td>
        <td className="align-middle py-1 px-0">
          {user?.roles === "admin" ? (
            // All four values on every tab: an admin can set pending, approved,
            // hold or rejected whatever stage the order is at. Approve and
            // reject used to be withheld until the customer had finished; that
            // restriction is gone here and in the endpoint.
            //
            // Approved on an early order is a PRE-approval -- it is recorded,
            // the order stays in its tab, and it lands at Approved once the
            // customer finishes. Nothing here promotes a row.
            <Dropdown>
              <Dropdown.Toggle
                className="px-2 py-0 rounded-3"
                variant={adminReviewedVariant(item?.WorkOrder?.adminReviewed)}
              >
                {toSentenceCase(item?.WorkOrder?.adminReviewed || "pending")}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {ADMIN_REVIEW_ALL.map((status, idx) => (
                  <Dropdown.Item
                    key={idx}
                    onClick={() => updateAdminReviewed(item?.woId, status)}
                  >
                    {toSentenceCase(status)}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          ) : (
            <span
              className={`badge px-2 py-1 bg-${adminReviewedVariant(
                item?.WorkOrder?.adminReviewed,
              )}`}
            >
              {toSentenceCase(item?.WorkOrder?.adminReviewed || "pending")}
            </span>
          )}
        </td>
        <td className="align-middle py-1">
          {getLocationInitials(
            item?.WorkOrder?.locationId,
            item?.WorkOrder?.location?.name,
          )}
        </td>
        <td className="align-middle py-1 text-nowrap">
          {item?.createdAt
            ? `${convertSqlTimestampToDate(item.createdAt, true, true)} - ${convertAttendanceRecordToTime(
                item.createdAt,
                false,
              )}`
            : "-"}
        </td>
        <td className="align-middle py-1 text-nowrap">
          {item?.lastOpened
            ? `${convertSqlTimestampToDate(item.lastOpened, true, true)} - ${convertAttendanceRecordToTime(
                item.lastOpened,
                false,
              )}`
            : "-"}
        </td>
        <td className="align-middle py-1 text-nowrap">
          {item?.approvalSentTime
            ? `${convertSqlTimestampToDate(item.approvalSentTime, true, true)} - ${convertAttendanceRecordToTime(
                item.approvalSentTime,
                false,
              )}`
            : "-"}
        </td>
        {/* Sent By. Plain text at the table's own size, like every other
            column -- a badge here read as a status, which it is not.

            An approval that was never sent has no sender and shows the same
            "-" the other columns use for absent values. */}
        <td className="align-middle py-1 text-nowrap">
          {senderInitials(item?.user) ? (
            <span title={senderFullName(item.user)}>
              {senderInitials(item.user)}
            </span>
          ) : (
            "-"
          )}
        </td>
        <td className="align-middle py-1 text-end d-flex justify-content-end">
          {/* Same shape as the QuickView header menu.

              popperConfig strategy "fixed" is not decoration: this table sits
              inside .table-responsive (overflow-x) AND an overflow-y wrapper,
              and an absolutely-positioned menu is clipped by both -- the exact
              fault that made the Nova Shipping three-dot menu unusable on
              2026-08-28. A fixed-strategy menu is positioned against the
              viewport instead, which plain overflow does not clip. */}
          <Dropdown className="me-1" align="end">
            <Dropdown.Toggle
              className="no-icon px-1"
              variant="primary"
              size="sm"
              id={`approval-actions-${item.id}`}
              title="More actions"
            >
              <i className="mdi mdi-dots-vertical" aria-hidden="true" />
            </Dropdown.Toggle>

            <Dropdown.Menu
              renderOnMount
              popperConfig={{ strategy: "fixed" }}
            >
              {/* SendWoApproval renders its own button, so the item is just a
                  wrapper -- padding stripped so the button fills it and the
                  row highlights like the others. */}
              <Dropdown.Item as="div" className="p-0 m-0">
                <SendWoApproval
                  woId={item.woId}
                  refreshFunc={() => {}}
                  btnClass={"btn w-100 text-start text-nowrap"}
                  btnTitle={item?.approvalSentTime ? "Resend" : "Send"}
                  // allApproved, not the card-derived isAllApproved above:
                  // it is the flag the server maintains and the one every
                  // other page's badge reads, so the button and the badges
                  // cannot disagree.
                  customerApproved={!!item?.allApproved}
                />
              </Dropdown.Item>

              {/* Same wrapper-and-button shape as the two SendWoApproval items
                  around it, NOT a plain Dropdown.Item.

                  A native .dropdown-item pads 1.5rem on the left while a .btn
                  pads 0.75rem, so mixing the two indented this one item further
                  than its neighbours. Matching the shape is what keeps the
                  three labels on one left edge. */}
              <Dropdown.Item as="div" className="p-0 m-0">
                <button
                  type="button"
                  className="btn w-100 text-start text-nowrap"
                  onClick={() => {
                    setSelectedApproval(item);
                    setShowDetailsAddModal(true);
                  }}
                >
                  Time Detail
                </button>
              </Dropdown.Item>

              {/* Same component as Resend above, with useQuestionTemplate --
                  that flag is what swaps the approval email for the question
                  one and retitles the dialog "Ask a Question For". Copied from
                  the work order details page, which already offers this from
                  its own three-dot menu, so the two behave identically rather
                  than becoming two ways of asking a customer something. */}
              <Dropdown.Item as="div" className="p-0 m-0">
                <SendWoApproval
                  woId={item.woId}
                  refreshFunc={() => {}}
                  btnClass={"btn w-100 text-start text-nowrap"}
                  btnTitle={"Ask a Question"}
                  useQuestionTemplate={true}
                />
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          {/* View and A stay on the row: they are the two that get used on
              nearly every row, and burying a link behind a menu costs a click
              on the common path. Everything else lives in the menu, which
              leads the group. */}
          <Link
            to={`/work-order/details/all/${item?.woId}?page=1`}
            state={{ workOrderIds: approvalData?.map((a) => a?.woId) || [] }}
            className="btn btn-sm btn-info me-1 text-nowrap"
          >
            View
          </Link>

          {/* Not-Sent rows have no approval yet, so there's nothing to open --
              render a disabled control rather than a link to
              /approvals/<companyId>/null. */}
          {item?.approvalLink ? (
            <a
              className="btn btn-sm btn-info"
              target="_blank"
              href={`/work-order/approvals/${user?.companyId}/${item.approvalLink}?viewOnly=true`}
              rel="noreferrer"
            >
              A
            </a>
          ) : (
            <button
              className="btn btn-sm btn-info"
              disabled
              title="No approval has been created for this work order yet"
            >
              A
            </button>
          )}


          {/* <DeleteModalButton
            btnClass={"btn btn-sm btn-danger ms-1 px-1"}
            deleteFunc={deleteApprovalData}
            deleteParams={item.id}
            disableConditions={loading}
          /> */}
        </td>
      </tr>
    );
  };

  return (
    <div className="" style={{ marginLeft: 260, height: "100vh" }}>
      <div className="bg-white shadow-sm p-2 mb-3">
        <div className="btn-group">
          {waStatusOptions.map((status) => (
            <div key={status} onClick={() => handleWaStatusChange(status)}>
              <button
                className={waStatus === status ? "btn btn-primary" : "btn "}
              >
                <span className="">
                  {formatWaStatusText(status)}
                  <span
                    style={{
                      padding: "0.1rem 0.5rem 0.16rem 0.3rem",
                    }}
                    className={`inline-block rounded-pill text-center ms-1 ${waStatus === status ? "bg-white text-dark" : "bg-primary text-white"}`}
                    data-testid={`tab-count-${status}`}
                  >
                    {` ${countFor(status)}`}
                  </span>
                </span>
              </button>
            </div>
          ))}
        </div>
      </div>

      {loading && <Spinner />}
      {/*   Table
       ********************************************* */}
      <div className="d-flex px-2 justify-content-between mb-3 align-items-center mx-2">
        <div>
          Show &nbsp;
          <div className="btn-group">
            <button
              className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start "
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {pageLimit}
            </button>
            <ul className="dropdown-menu w-25">
              <li onClick={() => setPageLimit(5)}>
                <button className="dropdown-item">5</button>
              </li>
              <li onClick={() => setPageLimit(10)}>
                <button className="dropdown-item">10</button>
              </li>
              <li onClick={() => setPageLimit(15)}>
                <button className="dropdown-item">15</button>
              </li>
              <li onClick={() => setPageLimit(20)}>
                <button className="dropdown-item">20</button>
              </li>
            </ul>
          </div>
        </div>

        <div className="d-flex flex-grow-1">
          {buttons()}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPageNumber(1);
              if (searchTerm && searchTerm !== "") {
                getApprovalData({ query: searchTerm, page: 1 });
              } else {
                getApprovalData({ clearSearch: true, page: 1 });
              }
            }}
            className="d-flex justify-content-end gap-1 align-items-center"
          >
            <input
              type="search"
              placeholder="Search by name..."
              value={searchTerm}
              className="form-control"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-info" type="submit">
              Search
            </button>
            <RefreshButton
              className="btn btn-outline-primary"
              loading={loading}
              onClick={() => getApprovalData({ page: pageNumber })}
            />
            {searchTerm !== "" && (
              <button
                className="btn btn-outline-danger"
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setLocation("");
                  setFilterBy({ name: "Filter by All", value: null });
                  setPageNumber(1);
                  getApprovalData({ clearSearch: true, page: 1 });
                }}
              >
                Clear
              </button>
            )}
          </form>
          {/* Add button moved next to search for consistent layout */}
        </div>
      </div>

      <div
        className="px-2 flex-grow-1 overflow-y-auto d-flex"
        style={{ height: "77vh", overflowY: "auto", borderRadius: 15 }}
      >
        <div className="horizontal-scrollable d-flex flex-grow-1">
          <TableOnly
            table_head={table_head}
            table_body={table_body}
            table_data={approvalData}
            tableSize="sm"
            actionsOnEnd={false}
            actionsOnCenter={true}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end mt-2">
        <PaginationWithState
          totalPages={totalPages}
          currPage={pageNumber}
          setCurrPage={setPageNumber}
        />
      </div>

      <Modal show={showDetailsAddModal} onHide={() => hideDetailsModal()}>
        <Modal.Header className="py-1" closeButton>
          <Modal.Title>Approval Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-1">
          {selectedApproval ? (
            <>
              {contactData && (
                <>
                  <h5 className="border-bottom border-2 w-fit pe-2">
                    Contact Details
                  </h5>
                  <div className="row">
                    <div className="col-6 fw-bold">Name :</div>
                    <div className="col-6">
                      {contactData?.firstName} {contactData?.lastName}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-6 fw-bold">Email :</div>
                    <div className="col-6">{contactData?.email}</div>
                  </div>
                  <div className="row">
                    <div className="col-6 fw-bold">Phone :</div>
                    <div className="col-6">{contactData?.phone}</div>
                  </div>
                </>
              )}
              <hr />
              <div>
                <h5 className="border-bottom border-2 w-fit pe-2">
                  Approval Details
                </h5>
                <div className="row mb-1">
                  <div className="col-6 fw-bold">WA-ID:</div>
                  <div className="col-6">WA-{selectedApproval.approvalId}</div>
                </div>
                <div className="row mb-1">
                  <div className="col-6 fw-bold">W-ID:</div>
                  <div className="col-6">W-{selectedApproval.woId}</div>
                </div>
                <div className="row mb-1">
                  <div className="col-6 fw-bold">Sent Time:</div>
                  <div className="col-6">
                    {selectedApproval?.approvalSentTime
                      ? `${convertSqlTimestampToDate(
                          selectedApproval.approvalSentTime,
                        )} - ${convertAttendanceRecordToTime(
                          selectedApproval.approvalSentTime,
                          false,
                        )}`
                      : "-"}
                  </div>
                </div>

                <div className="row mb-1">
                  <div className="col-6 fw-bold">Last Opened:</div>
                  <div className="col-6">
                    {selectedApproval?.lastOpened
                      ? `${convertSqlTimestampToDate(
                          selectedApproval.lastOpened,
                        )} - ${convertAttendanceRecordToTime(
                          selectedApproval.lastOpened,
                          false,
                        )}`
                      : "-"}
                  </div>
                </div>
                <div className="row mb-1">
                  <div className="col-6 fw-bold">Times Opened:</div>
                  <div className="col-6">
                    {selectedApproval?.timesOpened || "-"}
                  </div>
                </div>
                <div className="row mb-1">
                  <div className="col-6 fw-bold">Approve Time:</div>
                  <div className="col-6">
                    {selectedApproval?.approveTime
                      ? `${convertSqlTimestampToDate(
                          selectedApproval.approveTime,
                        )} - ${convertAttendanceRecordToTime(
                          selectedApproval.approveTime,
                          false,
                        )}`
                      : "-"}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <p>No approval selected</p>
          )}
          {/* Footer close Button */}
          <div className="text-end mt-2 border-top pt-2">
            <Button variant="secondary" onClick={() => hideDetailsModal()}>
              Close
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        size="lg"
        show={showJobNameModal}
        onHide={() => setShowJobNameModal(false)}
      >
        <Modal.Body>
          <form onSubmit={UpdateJobNames}>
            {loading && <Spinner />}
            <h4>Update Job Name / Sub Job Name for W-{selectedWOItem?.woId}</h4>
            <hr />
            <div className="my-3">
              <label>Job Name</label>
              <input
                className="form-control"
                value={jobNamesFormData?.jobName || ""}
                onChange={(e) =>
                  setJobNamesFormData((prev) => ({
                    ...prev,
                    jobName: e.target.value,
                  }))
                }
              />
            </div>
            <div className="my-3">
              <label>Sub Job Name</label>
              <input
                className="form-control"
                value={jobNamesFormData?.subJobName || ""}
                onChange={(e) =>
                  setJobNamesFormData((prev) => ({
                    ...prev,
                    subJobName: e.target.value,
                  }))
                }
              />
            </div>
            <hr className="mt-4" />
            <div className="text-end">
              <button
                type="button"
                className="btn btn-sm btn-secondary me-2"
                onClick={() => setShowJobNameModal(false)}
              >
                Close
              </button>
              <button className="btn btn-sm btn-primary">Update</button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}
